import { JSX, useEffect, useState } from "react";
import { Switch, Table, TableColumnsType, Tag, message } from "antd";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import Text from "../../components/Text/Text";
import PageTitleNested from "../../components/PageTitle/PageTitleNested";
import { CardContainer } from "../../components/Card/CardContainer";
import BreadCrumb from "../../components/BreadCrumb/BreadCrumb";
import Button from "../../components/Button/Button";
import ConfirmModal from "../../components/Modal/ConfirmModal";
import { GreyTableContainer } from "../../components/Table/GreyTable";
import { getTierProgramTierLevel, toggleRebatePercent } from "../../datasource/TierProgramDatasource";
import { color } from "../../resource";
import { count } from "console";
import { de } from "date-fns/locale";
import { toRebatePercent } from "./rebatePercent";
import { useRecoilValue } from "recoil";
import { checkPermission } from "../../components/Permission/Permission";
import { roleAtom } from "../../store/RoleAtom";

interface TierBenefit {
  tierLevelId: string;
  tierName: string;
  tierRank: number;
  minTarget: number;
  maxTarget: number | null;
  rebatePercent: number | null;
  isShowRebatePercent: boolean;
  benefitCount: number;
  reviewTriggerMode: "PERCENT" | string | null;
  reviewTriggerValue: number | null;
  isNotAutoDemotable: boolean;
}

interface TierProgramDetail {
  tierProgramId: string;
  year: number | string;
  status: "PUBLISHED" | "DRAFT" | "CLOSED" | string;
  updatedAt: string;
  updatedBy: string;
  levels: TierBenefit[];
}

const formatNumber = (value: number): string => value.toLocaleString("en-US");

function ShopTierBenefitPage(): JSX.Element {
  const navigate = useNavigate();
  const { year: programId } = useParams<{ year: string }>();
  const roleData = useRecoilValue(roleAtom);
  const canEdit = checkPermission(["shopTier", "edit"], roleData);
  const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showInAppMap, setShowInAppMap] = useState<Record<string, boolean>>({});

  const company = JSON.parse(localStorage.getItem("profile") || "null")?.company;
  const rebateLabel = company === "ICPF" ? "Top Up" : "Rebate";
  const rebateUnit = company === "ICPL" ? "(M฿)" : "(ตัน)";
  const divideMillion = (value: number | null): number | null => {
    if (value === null || value === undefined) return null;
    if (company === "ICPL") {
      return Number((value / 1000000).toFixed(2));
    }
    return value;
  }
  const userProfile = JSON.parse(localStorage.getItem("profile")!);

  const { data: tierProgramDetail, isLoading } = useQuery(
    ["tier-program-tier-level", programId],
    () => getTierProgramTierLevel(programId as string),
    { enabled: !!programId, refetchOnWindowFocus: false },
  );

  const formatThreshold = (min: number, max: number | null): string => {
    if(company === "ICPL") {
      if (max === null || max === undefined) return `${formatNumber(min)} ขึ้นไป`;
      if (min === 0) return `ต่ำกว่า ${formatNumber(max)}`;
      return `${formatNumber(min)} - ${formatNumber(max-1)}`;
    }
    if (max === null || max === undefined) return `${min} ขึ้นไป`;
    return `${min} - ${max}`;
  };

  const detail: TierProgramDetail | undefined = tierProgramDetail?.responseData;
  const tiers: TierBenefit[] = detail?.levels ?? [];

  useEffect(() => {
    if (!tiers.length) return;
    setShowInAppMap(
      tiers.reduce<Record<string, boolean>>((acc, tier) => {
        acc[tier.tierLevelId] = tier.isShowRebatePercent;
        return acc;
      }, {}),
    );
  }, [tiers]);

  const handleToggleShowInApp = (tierLevelId: string, checked: boolean): void => {
    toggleRebatePercent(tierLevelId, checked, `${userProfile?.firstname} ${userProfile?.lastname}`).then((res)=> {
      setShowInAppMap((prev) => ({ ...prev, [tierLevelId]: checked }));
    });
  };

  const tierColumns: TableColumnsType<TierBenefit> = [
    {
      title: "ระดับ",
      dataIndex: "tierName",
      key: "tierName",
      render: (tierName: string) => (
        <Text fontFamily="Sarabun" fontSize={16} fontWeight={700} color="Text2">{tierName}</Text>
      ),
    },
    {
      title:  `เกณฑ์ยอดสะสม ${rebateUnit}`,
      dataIndex: "minTarget",
      key: "threshold",
      render: (_v: number, record: TierBenefit) => (
        <Text fontFamily="Sarabun" fontSize={16} color="Text2">
          {formatThreshold(divideMillion(record.minTarget)!,  divideMillion(record.maxTarget))}
        </Text>
      ),
    },
    {
      title: `${rebateLabel} %`,
      dataIndex: "rebatePercent",
      key: "rebatePercent",
      render: (v: number | null, record: TierBenefit) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Text fontFamily="Sarabun" fontSize={16} color="Text2">{toRebatePercent(v)}</Text>
          {!showInAppMap[record.tierLevelId] && (
            <Tag style={{ borderRadius: "16px" }} color={color.background1}>
              <Text fontSize={12} color="Disable" fontWeight={700}>ซ่อนจากร้านค้า</Text>
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: `แสดง % ${rebateLabel} ในแอปร้านค้า`,
      dataIndex: "isShowRebatePercent",
      key: "isShowRebatePercent",
      align: "center",
      render: (_v: boolean, record: TierBenefit) => (
        detail?.status === "CLOSED" ?
        <Text fontSize={16} fontWeight={500} color="Text3">{showInAppMap[record.tierLevelId] ? "แสดง" : "ซ่อน"}</Text> :
        <Switch
          checked={!!showInAppMap[record.tierLevelId]}
          onChange={(checked) => handleToggleShowInApp(record.tierLevelId, checked)}
        />
      ),
    },
    {
      title: "สิทธิประโยชน์",
      dataIndex: "benefitCount",
      key: "benefitCount",
      render: (count: number) =>
        count > 0 ? (
          <Text fontFamily="Sarabun" fontSize={16} fontWeight={500} color="Text2">{count} รายการ</Text>
        ) : (
          <Text fontFamily="Sarabun" fontSize={16} color="Text3">ไม่มีสิทธิประโยชน์</Text>
        ),
    },
    {
      title: "เงื่อนไขการพิจารณา",
      dataIndex: "reviewTriggerMode",
      key: "reviewTrigger",
      render: (mode: string | null, record: TierBenefit) => {
        if (!mode) {
          return <Text fontSize={16} color="Text2">ระดับตั้งต้น</Text>;
        }
        if (mode !== "PERCENT") {
          return <Text fontSize={16} color="Text2">พิจารณาทุกร้าน</Text>;
        }
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: "1px" }}>
            <Text fontSize={16} color="Text2">
              {`เข้าคิวรอพิจารณาปรับระดับสิ้นปีที่`} {record.reviewTriggerValue ?? "-"}{"%"}
            </Text>
            {
              record.isNotAutoDemotable && (
                <Text fontFamily="Sarabun" fontSize={12} color="Text3">
                  {`ล็อกไม่ให้ระบบลดเอง`}
                </Text>
              )
            }
          </div>
        );
      },
    },
  ];

  const yearLabel = detail?.year ? `ปี ${detail.year}` : "";
  const statusLabel = (status : string) => {
    switch (status) {
      case "PUBLISHED":
        return "ร้านใช้สิทธิ์ชุดนี้อยู่";
      case "PREVIEW":
        return "ร้านเห็นล่วงหน้าแล้ว";
      case "DRAFT":
        return "เตรียมไว้ ร้านยังไม่เห็น";
      case "CLOSED":
        return "จบรอบแล้ว";
      default:
        return "เตรียมไว้ ร้านยังไม่เห็น";
    }
  }

  return (
    <CardContainer>
      <PageTitleNested
        title={`สิทธิประโยชน์${yearLabel}`}
        showBack
        onBack={() => navigate(-1)}
        customBreadCrumb={
          <BreadCrumb
            data={[
              { text: "ตั้งค่าระดับ", path: -1 },
              { text: `สิทธิประโยชน์${yearLabel}`, path: window.location.pathname },
            ]}
          />
        }
        description={
          <Text fontFamily="Sarabun" fontSize={14} color="Text3">
            {statusLabel(detail?.status || "")} · แก้ไขล่าสุด {detail?.updatedAt ? dayjs(detail.updatedAt).format("DD/MM/YYYY HH:mm") : "-"} โดย {detail?.updatedBy === "SYSTEM" ? "ระบบ" : detail?.updatedBy || "-"}
          </Text>
        }
        extra={
          canEdit && detail?.status != "CLOSED" ? (
            <Button
              icon={<EditOutlined />}
              typeButton="primary"
              title="แก้ไขสิทธิประโยชน์"
              style={{ width: "auto" }}
              onClick={() => navigate(`/ShopTier/edit/benefit/${programId}`)}
            />
          ) : (
            <Button
              icon={<EyeOutlined />}
              typeButton="primary"
              title="ดูรายละเอียด"
              style={{ width: "auto" }}
              onClick={() => navigate(`/ShopTier/view/benefit/${programId}`)}
            />
          )
        }
      />

      <div style={{ marginTop: 20 }}>
        <GreyTableContainer>
          <Table
            rowKey="tierLevelId"
            dataSource={tiers}
            columns={tierColumns}
            loading={isLoading}
            pagination={false}
          />
        </GreyTableContainer>
      </div>

      <ConfirmModal
        visible={showDuplicateConfirm}
        title="ยืนยันการทำสำเนา"
        desc={`สร้างสำเนาสิทธิประโยชน์${yearLabel} เป็นปีใหม่ ระบบจะคัดลอกเกณฑ์และสิทธิประโยชน์ทั้งหมดของปีนี้ไปยังปีที่สร้างใหม่`}
        onConfirm={() => {
          message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา");
          setShowDuplicateConfirm(false);
        }}
        onCancel={() => setShowDuplicateConfirm(false)}
      />

      <ConfirmModal
        visible={showDeleteConfirm}
        title="ยืนยันการลบสิทธิประโยชน์"
        desc={`ลบสิทธิประโยชน์${yearLabel} ออกจากระบบถาวร ร้านค้าจะไม่เห็นสิทธิประโยชน์นี้อีก และไม่สามารถกู้คืนได้`}
        confirmText="ลบ"
        typeButtonSubmit="danger"
        onConfirm={() => {
          message.info("ฟังก์ชันนี้อยู่ระหว่างการพัฒนา");
          setShowDeleteConfirm(false);
        }}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </CardContainer>
  );
}

export default ShopTierBenefitPage;
